import { useState } from 'react'
import { motion } from 'framer-motion'
import { MessageCircle, MapPin, Send, Lock } from 'lucide-react'

const serviceOptions = [
  'Motorista Particular',
  'Freteiro',
  'Reparos e Instalação Elétricos',
  'Reparos e Instalação Hidráulicos',
  'Montagem de Móveis',
  'Pinturas em Geral',
]

const CTAFormSection = () => {
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    servico: '',
    descricao: '',
  })

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const text =
      `Olá! Gostaria de solicitar um orçamento.%0A%0A` +
      `*Nome:* ${encodeURIComponent(form.nome)}%0A` +
      `*Telefone:* ${encodeURIComponent(form.telefone)}%0A` +
      `*Serviço:* ${encodeURIComponent(form.servico)}%0A` +
      `*Descrição:* ${encodeURIComponent(form.descricao)}`
    window.open(`https://wa.me/5547988114676?text=${text}`, '_blank')
  }

  const inputBase =
    'w-full bg-white/10 border border-white/20 text-white placeholder-white/40 px-4 py-3.5 text-sm focus:outline-none focus:border-[#F2B300] transition-colors duration-200'

  return (
    <section
      id="formulario"
      data-testid="cta-form-section"
      className="py-24 md:py-32 bg-[#062B63] relative overflow-hidden"
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(242,179,0,0.12) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      {/* Gold top line */}
      <div className="absolute top-0 left-0 w-full h-px bg-[#F2B300]/15" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">

          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[#F2B300] text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
              Contato
            </p>

            <div className="overflow-hidden mb-2">
              <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-white font-outfit tracking-tight leading-[0.93]">
                Vamos resolver
              </h2>
            </div>
            <div className="overflow-hidden mb-8">
              <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-[#F2B300] font-outfit tracking-tight leading-[0.93]">
                isso para você!
              </h2>
            </div>

            <p className="text-white/55 text-base lg:text-lg leading-relaxed mb-10 max-w-[360px]">
              Preencha o formulário ou fale conosco no WhatsApp. Atendimento rápido, claro e sem compromisso.
            </p>

            {/* Contact info */}
            <div className="space-y-5">
              <a
                href="https://wa.me/5547988114676"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <div className="w-11 h-11 bg-[#F2B300]/15 border border-[#F2B300]/25 flex items-center justify-center flex-shrink-0 group-hover:bg-[#F2B300]/25 transition-colors">
                  <MessageCircle size={18} className="text-[#F2B300]" />
                </div>
                <div>
                  <div className="text-[10px] text-white/35 uppercase tracking-wider mb-0.5 font-inter">
                    WhatsApp
                  </div>
                  <div className="text-white font-semibold text-base group-hover:text-[#F2B300] transition-colors">
                    (47) 98811-4676
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-[#F2B300]/15 border border-[#F2B300]/25 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-[#F2B300]" />
                </div>
                <div>
                  <div className="text-[10px] text-white/35 uppercase tracking-wider mb-0.5 font-inter">
                    Região atendida
                  </div>
                  <div className="text-white/75 text-base">
                    Balneário Camboriú e região
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-3.5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <input
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  placeholder="Nome"
                  required
                  data-testid="form-nome"
                  className={inputBase}
                  aria-label="Seu nome"
                />
                <input
                  type="tel"
                  name="telefone"
                  value={form.telefone}
                  onChange={handleChange}
                  placeholder="Telefone (com DDD)"
                  required
                  data-testid="form-telefone"
                  className={inputBase}
                  aria-label="Seu telefone com DDD"
                />
              </div>

              <div className="relative">
                <select
                  name="servico"
                  value={form.servico}
                  onChange={handleChange}
                  required
                  data-testid="form-servico"
                  aria-label="Selecione o serviço"
                  className={`${inputBase} appearance-none cursor-pointer pr-10`}
                  style={{ background: 'rgba(255,255,255,0.08)' }}
                >
                  <option value="" disabled style={{ background: '#062B63', color: '#fff' }}>
                    Serviço
                  </option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s} style={{ background: '#062B63', color: '#fff' }}>
                      {s}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="#F2B300" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>

              <textarea
                name="descricao"
                value={form.descricao}
                onChange={handleChange}
                placeholder="Descreva o que precisa"
                required
                rows={4}
                data-testid="form-descricao"
                aria-label="Descreva o serviço"
                className={`${inputBase} resize-none`}
              />

              <button
                type="submit"
                data-testid="form-submit-btn"
                className="w-full bg-[#25D366] text-white px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#20ba5a] transition-colors flex items-center justify-center gap-2.5 active:scale-[0.99]"
              >
                <Send size={16} />
                Quero meu orçamento
              </button>

              <p className="flex items-center justify-center gap-1.5 text-white/35 text-xs pt-1">
                <Lock size={11} />
                Seus dados estão protegidos.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default CTAFormSection
